/**
 * Main-thread side of the orbit engine: promise RPC over one persistent
 * primary worker plus a lazily created pool used only for large
 * contact-window runs.
 *
 * Port of game/js/loader/loader-worker.js. Every worker keeps a map of
 * pending requests keyed by message id and its onmessage handler is set once,
 * so concurrent jobs on the same worker (including pooled runs) resolve to the
 * right caller; the original overwrote onmessage per call.
 */
import type { ContactWindow, ContactWindowMap, MaskPoint } from '@/features/loader/types';
import type {
  EngineMessageType,
  EngineObserver,
  EngineProgress,
  EngineRequests,
  EngineResponse,
  EngineResults,
  EngineSatellite,
  LiveTickResult,
  ProgressCallback,
  PropagateResult,
  RawContactWindowMap,
  SatBandEntry,
} from './orbit.core';

export type { EngineObserver, EngineProgress, EngineSatellite, LiveTickResult, PropagateResult, MaskPoint };

export interface ContactWindowRequest {
  /** Scan elevation visibility without band eligibility (pass inspector only). */
  visibilityOnly?: boolean;
  missions: { id: string; tle1: string; tle2: string; bandType?: string; bandTypes?: string[] }[];
  antennas: { id: string; lat: number; lon: number; elevationMinDeg?: number; bands?: string[] }[];
  startMs: number;
  endMs: number;
  stepSeconds?: number;
  minElevDeg?: number;
  onProgress?: (p: EngineProgress) => void;
}

/** The subset of the Worker interface the engine uses; tests supply an in-process stand-in. */
export interface WorkerLike {
  postMessage(message: unknown, transfer?: Transferable[]): void;
  onmessage: ((event: MessageEvent) => void) | null;
  onerror: ((event: ErrorEvent) => void) | null;
  terminate(): void;
}

export type WorkerFactory = () => WorkerLike;

export interface OrbitEngineOptions {
  /** Pool workers for contactWindows; defaults to defaultPoolSize(). */
  poolSize?: number;
  /** Mission count above which contactWindows fans out to the pool; defaults to POOL_THRESHOLD. */
  poolThreshold?: number;
}

export const POOL_THRESHOLD = 20;

export const MAX_CONTACT_WORKERS = 4;

/** Leave memory/CPU headroom for Cesium's own workers and live propagation. */
export function defaultPoolSize(): number {
  const cores = typeof navigator !== 'undefined' ? navigator.hardwareConcurrency : undefined;
  return Math.min(MAX_CONTACT_WORKERS, Math.max(1, cores ? cores - 1 : 3));
}

interface PendingRequest {
  resolve: (value: unknown) => void;
  reject: (reason: Error) => void;
  onProgress?: ProgressCallback;
}

/** One worker with id-keyed pending requests. */
class RpcWorker {
  private readonly worker: WorkerLike;
  private readonly pending = new Map<number, PendingRequest>();
  private nextId = 0;

  constructor(worker: WorkerLike) {
    this.worker = worker;
    worker.onmessage = (event) => this.onMessage(event.data as EngineResponse);
    worker.onerror = (event) => this.failAll(new Error('Worker error: ' + (event.message || 'Unknown')));
  }

  get inFlight(): number {
    return this.pending.size;
  }

  send<T extends EngineMessageType>(
    type: T,
    data: EngineRequests[T],
    onProgress?: ProgressCallback,
  ): Promise<EngineResults[T]> {
    const id = ++this.nextId;
    return new Promise<EngineResults[T]>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (value: unknown) => void, reject, onProgress });
      this.worker.postMessage({ type, id, data });
    });
  }

  private onMessage(msg: EngineResponse): void {
    const id = msg.id ?? -1;
    const pending = this.pending.get(id);
    if (!pending) {
      console.warn('[OrbitEngine] response for unknown request id', id, msg.type);
      return;
    }
    if (msg.type === 'progress') {
      pending.onProgress?.(msg.data);
      return;
    }
    this.pending.delete(id);
    if (msg.type === 'result') pending.resolve(msg.data);
    else pending.reject(new Error(msg.error));
  }

  private failAll(error: Error): void {
    const all = Array.from(this.pending.values());
    this.pending.clear();
    for (const p of all) p.reject(error);
  }

  terminate(): void {
    this.failAll(new Error('Worker terminated'));
    this.worker.terminate();
  }
}

function toContactWindow(raw: {
  start: number;
  end: number;
  maxElevation: number;
  durationMin: number;
}): ContactWindow {
  return { startMs: raw.start, endMs: raw.end, maxElevation: raw.maxElevation, durationMin: raw.durationMin };
}

function convertWindowResult(result: RawContactWindowMap): ContactWindowMap {
  const converted: ContactWindowMap = {};
  for (const missionId of Object.keys(result)) {
    const perAntenna: Record<string, ContactWindow[]> = {};
    for (const antennaId of Object.keys(result[missionId])) {
      perAntenna[antennaId] = result[missionId][antennaId].map(toContactWindow);
    }
    converted[missionId] = perAntenna;
  }
  return converted;
}

type ContactWindowBase = Omit<EngineRequests['contactWindows'], 'missions'>;

export class OrbitEngine {
  private frameListeners = new Set<(timestamp: number) => void>();
  onFrame(listener: (timestamp: number) => void): () => void {
    this.frameListeners.add(listener);
    return () => { this.frameListeners.delete(listener); };
  }
  neighbourhood(request: EngineRequests['neighbourhood']) { return this.getPrimary().send('neighbourhood', request); }
  trajectory(request: EngineRequests['trajectory']) { return this.getPrimary().send('trajectory', request); }
  private readonly factory: WorkerFactory;
  private readonly poolSize: number;
  private readonly poolThreshold: number;
  private primary: RpcWorker | null = null;
  private pool: RpcWorker[] | null = null;

  constructor(factory: WorkerFactory, options: OrbitEngineOptions = {}) {
    this.factory = factory;
    this.poolSize = options.poolSize ?? defaultPoolSize();
    this.poolThreshold = options.poolThreshold ?? POOL_THRESHOLD;
  }

  /** Number of pool workers spawned so far (0 until the first pooled run). */
  get poolWorkerCount(): number {
    return this.pool ? this.pool.length : 0;
  }

  private getPrimary(): RpcWorker {
    if (!this.primary) this.primary = new RpcWorker(this.factory());
    return this.primary;
  }

  private getPool(): RpcWorker[] {
    if (!this.pool) {
      const pool: RpcWorker[] = [];
      for (let i = 0; i < this.poolSize; i++) pool.push(new RpcWorker(this.factory()));
      this.pool = pool;
    }
    return this.pool;
  }

  /** Replaces the tracked catalogue. */
  init(sats: EngineSatellite[]): Promise<{ count: number; failed: number; totalTracked: number }> {
    return this.getPrimary().send('init', { satellites: sats });
  }

  addSatellites(sats: EngineSatellite[]): Promise<{ added: number; totalTracked: number }> {
    return this.getPrimary().send('addSatellites', { satellites: sats });
  }

  removeSatellites(ids: string[]): Promise<{ removed: number; totalTracked: number }> {
    return this.getPrimary().send('removeSatellites', { ids });
  }

  async propagate(timestampMs: number): Promise<PropagateResult> {
    const result = await this.getPrimary().send('propagate', { timestamp: timestampMs });
    for (const listener of this.frameListeners) listener(result.timestamp);
    return result;
  }

  /**
   * Contact windows for every mission/antenna pair. Up to poolThreshold
   * missions (or a pool of one) run on the primary worker; larger runs are
   * split into ceil(missions / n) chunks across the pool and merged.
   */
  async computeContactWindows(req: ContactWindowRequest): Promise<ContactWindowMap> {
    const missions = req.missions.map((m) => ({
      id: m.id,
      tle1: m.tle1,
      tle2: m.tle2,
      bandType: m.bandType,
      bandTypes: m.bandTypes,
    }));
    const base: ContactWindowBase = {
      visibilityOnly: req.visibilityOnly,
      antennas: req.antennas.map((a) => ({
        id: a.id,
        lat: a.lat,
        lon: a.lon,
        elevationMinDeg: a.elevationMinDeg,
        bands: a.bands ?? [],
      })),
      startTime: req.startMs,
      endTime: req.endMs,
      stepSeconds: req.stepSeconds ?? 30,
      minElevDeg: req.minElevDeg,
    };

    if (missions.length <= this.poolThreshold || this.poolSize <= 1) {
      const raw = await this.getPrimary().send('contactWindows', { missions, ...base }, req.onProgress);
      return convertWindowResult(raw);
    }
    return this.computeContactWindowsPooled(missions, base, req.onProgress);
  }

  private async computeContactWindowsPooled(
    missions: EngineRequests['contactWindows']['missions'],
    base: ContactWindowBase,
    onProgress?: ProgressCallback,
  ): Promise<ContactWindowMap> {
    const pool = this.getPool();
    const slots = Math.min(pool.length, missions.length);
    const chunkSize = Math.ceil(missions.length / slots);
    const chunks: (typeof missions)[] = [];
    for (let i = 0; i < missions.length; i += chunkSize) chunks.push(missions.slice(i, i + chunkSize));
    const n = chunks.length;

    const progress = new Array<number>(n).fill(0);
    const reportProgress = () => {
      if (!onProgress) return;
      let sum = 0;
      for (const p of progress) sum += p;
      let avg = Math.round(sum / n);
      if (avg > 99) avg = 99;
      onProgress({ percent: avg, message: `Computing contacts... ${avg}% (${n} workers)` });
    };

    const results = await Promise.all(
      chunks.map((chunk, idx) =>
        pool[idx]
          .send('contactWindows', { missions: chunk, ...base }, (p) => {
            progress[idx] = p.percent;
            reportProgress();
          })
          .then((result) => {
            progress[idx] = 100;
            reportProgress();
            return result;
          }),
      ),
    );

    const merged: RawContactWindowMap = {};
    for (const slice of results) {
      for (const key of Object.keys(slice)) merged[key] = slice[key];
    }
    return convertWindowResult(merged);
  }

  setLiveObservers(observers: EngineObserver[]): Promise<{ observerCount: number }> {
    return this.getPrimary().send('setLiveObservers', { antennas: observers });
  }

  setSatBands(sats: SatBandEntry[]): Promise<{ count: number }> {
    return this.getPrimary().send('setSatBands', { sats });
  }

  liveTick(timestampMs: number): Promise<LiveTickResult> {
    return this.getPrimary().send('liveTick', { timestamp: timestampMs });
  }

  async resetLiveContacts(): Promise<void> {
    await this.getPrimary().send('resetLiveContacts', undefined);
  }

  async clearCache(): Promise<void> {
    await this.getPrimary().send('clearCache', undefined);
  }

  /** Terminates the primary worker and the pool; pending requests reject. The next call spawns fresh workers. */
  terminate(): void {
    if (this.primary) {
      this.primary.terminate();
      this.primary = null;
    }
    if (this.pool) {
      for (const worker of this.pool) worker.terminate();
      this.pool = null;
    }
  }
}

/** Browser worker factory: Vite bundles orbit.worker.ts from this URL pattern. */
export function browserWorkerFactory(): WorkerLike {
  return new Worker(new URL('./orbit.worker.ts', import.meta.url), { type: 'module' });
}

export function createOrbitEngine(workerFactory: WorkerFactory, options?: OrbitEngineOptions): OrbitEngine {
  return new OrbitEngine(workerFactory, options);
}

let singleton: OrbitEngine | null = null;

/** The app-wide engine on the real module worker. */
export function getOrbitEngine(): OrbitEngine {
  if (!singleton) singleton = new OrbitEngine(browserWorkerFactory);
  return singleton;
}
